import React, { useState, useEffect, useRef, memo } from 'react';
import { Camera, Upload, User } from 'lucide-react';
import { usePhotoContext } from '@/contexts/PhotoContext';
import { fixedUnifiedPhotoService } from '@/services/FixedUnifiedPhotoService';
import { getOptimizedImageUrl, type CloudinaryOptions } from '@/utils/cloudinaryOptimizer';
import { useCurrentUser, useUser } from '@/contexts/NetworkDataContext';
import { normalizeCloudinaryBaseUrl } from '@/utils/cloudinaryCanonical';
import {
  getCachedUrl,
  invalidateUserPhotoCache,
  makePhotoKey,
  setCachedUrl,
} from '@/lib/userPhotoSessionCache';
import { attemptRecoverSelfProfilePhoto } from '@/lib/selfProfilePhotoRecovery';
import ProfileHeroPhotoEmpty from './ProfileHeroPhotoEmpty';

/**
 * V160.14.3: Phase 3 - Component Integration with WebP Optimization & Deduplication
 * Integrates Phase 1 (WebP optimizer) and Phase 2 (cache deduplication) into UserPhoto component
 *
 * Flow: Database → getCachedImageUrl(with userId + WebP options) → Optimized WebP → Deduplicated Cache
 */

interface UserPhotoProps {
  userId: string;
  size?: 'small' | 'medium' | 'large' | 'xl';
  className?: string;
  fallback?: string;
  onClick?: () => void;
  showUploadButton?: boolean;
  onUploadComplete?: (photoUrl: string) => void;
  userName?: string;
  // V218: Allow passing photo_urls directly to bypass cache lookup
  photoUrls?: string[];
}

// V160.14.3: Size configuration with Cloudinary optimization targets
const SIZE_CONFIG = {
  small: {
    width: 128,
    height: 128,
    className: 'w-8',
    cloudinarySize: 128
  },
  medium: {
    width: 256,
    height: 256,
    className: 'w-16',
    cloudinarySize: 256
  },
  large: {
    width: 384,
    height: 384,
    className: 'w-24',
    cloudinarySize: 384
  },
  xl: {
    width: 512,
    height: 512,
    className: '',
    cloudinarySize: 512
  },
};

const borderClasses = {
  small: 'border-2',
  medium: 'border-2',
  large: 'border-3',
  xl: 'border-4'
};

/**
 * V150.34: Multi-Tier Photo Loading Component
 */
const UserPhotoComponent: React.FC<UserPhotoProps> = ({
  userId,
  size = 'medium',
  className = '',
  fallback,
  onClick,
  showUploadButton = false,
  onUploadComplete,
  userName = 'User',
  photoUrls: propsPhotoUrls
}) => {
  // V325.17: Only use context for upload writes. Loading state is local to prevent render thrashing.
  const { updateUserPhoto } = usePhotoContext();

  // V325.19: useProfilePhoto removed — was called per instance, causing context cascade.
  // URL sources: propsPhotoUrls (from parent) > cachedUser.photo_urls (from NetworkDataContext)
  // Profile.tsx passes the unified URL via photoUrls prop for the current user's hero.
  const unifiedPhotoUrl: string | null = null;

  // V325.23: Move useUser BEFORE initialOptimizedUrl so sync initializer
  // can use context data when props aren't passed
  const cachedUser = useUser(userId);
  const currentUser = useCurrentUser();
  const propsFirstUrl = propsPhotoUrls?.[0];
  const cachedFirstUrl = cachedUser?.photo_urls?.[0];

  // V220: Track viewport size for responsive image loading (must be before variant/dims)
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  // V325.12: Variant & canonical dimensions
  const isHeroContext = size === 'xl' && (className?.includes('!w-full') || className?.includes('w-full'));
  const variant: 'avatar' | 'hero' = isHeroContext ? 'hero' : 'avatar';
  const isMobile = viewportWidth < 768;

  const targetDims = (() => {
    if (variant === 'hero') {
      return { w: isMobile ? 280 : 800, h: isMobile ? 350 : 1000 };
    }
    return { w: 256, h: 320 };
  })();

  const cacheKey = userId ? makePhotoKey(userId, variant, targetDims.w, targetDims.h) : '';

  // V325.12: Optimized initial state
  const initialOptimizedUrl = (() => {
    if (userId && cacheKey) {
      const cached = getCachedUrl(cacheKey);
      if (cached) return cached;
    }

    // V325.23: Check props first, then context data — ensures first-paint has URL
    const firstUrl = propsFirstUrl || cachedFirstUrl;
    if (firstUrl && typeof firstUrl === 'string' && firstUrl.startsWith('http')) {
      const url = firstUrl;
      const options: CloudinaryOptions = {
        width: targetDims.w,
        height: targetDims.h,
        crop: 'fill',
        gravity: 'faces',
        quality: 'auto',
        format: 'webp',
      };
      const baseUrl = normalizeCloudinaryBaseUrl(url);
      const optimized = getOptimizedImageUrl(baseUrl, options);
      if (userId && cacheKey) setCachedUrl(cacheKey, optimized);
      return optimized;
    }

    return '';
  })();

  const [displayUrl, setDisplayUrl] = useState<string>(initialOptimizedUrl);
  const [isLoading, setIsLoading] = useState(!initialOptimizedUrl);
  const [imageError, setImageError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fallbackTriggered, setFallbackTriggered] = useState(false);

  // V325.15: Stable primitives to prevent effect identity churn (cachedUser/propsFirstUrl/cachedFirstUrl moved above)
  const hasLoadedRef = useRef(!!initialOptimizedUrl);
  const loadStartTime = useRef(Date.now());

  // V325.15: Track latest displayUrl without triggering loadPhoto effect loops
  const displayUrlRef = useRef<string>(initialOptimizedUrl);
  useEffect(() => { displayUrlRef.current = displayUrl; }, [displayUrl]);

  // V220: Update viewport width on resize (for profile hero responsive images)
  useEffect(() => {
    const isProfileHero = size === 'xl' && (className.includes('!w-full') || className.includes('w-full'));
    if (!isProfileHero) return; // Only track viewport for profile hero photos

    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [size, className]);

  // V325.15: Stabilized photo loading — primitives only, canonical URLs, no loops
  useEffect(() => {
    const loadPhoto = () => {
      if (!userId) return;

      // V325.18: If we already have a valid URL, don't re-process
      // This prevents remount cycles from causing re-fetches
      if (hasLoadedRef.current && displayUrlRef.current && !fallbackTriggered) {
        setIsLoading(false);
        return;
      }

      loadStartTime.current = Date.now();

      try {
        // 1. Early Cache Check (fastest path)
        if (cacheKey) {
          const cached = getCachedUrl(cacheKey);
          if (cached) {
            if (!hasLoadedRef.current || cached !== displayUrlRef.current) {
              setDisplayUrl(cached);
              displayUrlRef.current = cached;
            }
            setIsLoading(false);
            hasLoadedRef.current = true;
            return;
          }
        }

        // 2. Resolve source URL from stable primitives (priority: props > unified > cached)
        let originalUrl = '';
        if (typeof propsFirstUrl === 'string' && propsFirstUrl) originalUrl = propsFirstUrl;
        else if (typeof unifiedPhotoUrl === 'string' && unifiedPhotoUrl) originalUrl = unifiedPhotoUrl;
        else if (typeof cachedFirstUrl === 'string' && cachedFirstUrl) originalUrl = cachedFirstUrl;

        if (!originalUrl) {
          if (displayUrlRef.current !== '') {
            setDisplayUrl('');
            displayUrlRef.current = '';
          }
          setIsLoading(false);
          hasLoadedRef.current = true;
          return;
        }

        // 3. Blob fallback: if blob URL failed, use Cloudinary URL from cache
        if (originalUrl.startsWith('blob:') && fallbackTriggered) {
          if (typeof cachedFirstUrl === 'string' && cachedFirstUrl.startsWith('http')) {
            const options: CloudinaryOptions = {
              width: targetDims.w,
              height: targetDims.h,
              crop: 'fill',
              gravity: 'faces',
              quality: 'auto',
              format: 'webp',
            };
            const baseCloud = normalizeCloudinaryBaseUrl(cachedFirstUrl);
            const optimizedFallback = getOptimizedImageUrl(baseCloud, options);

            if (!hasLoadedRef.current || optimizedFallback !== displayUrlRef.current) {
              setDisplayUrl(optimizedFallback);
              displayUrlRef.current = optimizedFallback;
            }
            setIsLoading(false);
            hasLoadedRef.current = true;
            if (cacheKey && optimizedFallback) setCachedUrl(cacheKey, optimizedFallback);
            return;
          }
        }

        // 4. Canonical optimize: normalize → then transform
        const options: CloudinaryOptions = {
          width: targetDims.w,
          height: targetDims.h,
          crop: 'fill',
          gravity: 'faces',
          quality: 'auto',
          format: 'webp',
        };
        const baseUrl = originalUrl.startsWith('blob:')
          ? originalUrl
          : normalizeCloudinaryBaseUrl(originalUrl);
        const optimizedUrl = getOptimizedImageUrl(baseUrl, options);

        // 5. Early exit if unchanged
        if (hasLoadedRef.current && optimizedUrl === displayUrlRef.current) return;

        // 6. Update state & cache
        setDisplayUrl(optimizedUrl);
        displayUrlRef.current = optimizedUrl;
        setIsLoading(false);
        hasLoadedRef.current = true;

        if (cacheKey && optimizedUrl && !optimizedUrl.startsWith('blob:')) {
          setCachedUrl(cacheKey, optimizedUrl);
        }
      } catch (error) {
        console.error('Photo load failed:', error);
        setDisplayUrl('');
        displayUrlRef.current = '';
        setIsLoading(false);
        hasLoadedRef.current = true;
      }
    };

    loadPhoto();
  }, [
    userId,
    cacheKey,            // stable string
    targetDims.w,        // stable number (avatars won't re-render on resize)
    targetDims.h,        // stable number
    propsFirstUrl,       // stable string (NOT the array)
    unifiedPhotoUrl,     // stable string
    cachedFirstUrl,      // stable string (NOT cachedUser object)
    fallbackTriggered,
    // DO NOT ADD: viewportWidth, displayUrl, propsPhotoUrls, cachedUser?.photo_urls,
    //             updateUserPhoto, size, className
  ]);

  // V150.34: Listen for photo updates
  useEffect(() => {
    const handlePhotoUpdate = (event: CustomEvent) => {
      if (event.detail.userId === userId) {
        invalidateUserPhotoCache(userId);
        setDisplayUrl(event.detail.photoUrl);
        setIsLoading(false);
        setImageError(false);
      }
    };

    window.addEventListener('photoUpdated', handlePhotoUpdate as EventListener);
    return () => window.removeEventListener('photoUpdated', handlePhotoUpdate as EventListener);
  }, [userId]);



  // V150.25: REMOVED - Old conflicting loading logic



  /**
   * Handle photo upload
   */
  const handleUpload = async (file: File) => {
    if (!userId) return;

    setIsUploading(true);
    console.log('V150.17: Starting photo upload for user:', userId);

    try {
      const result = await fixedUnifiedPhotoService.uploadPhoto(
        userId,
        file,
        (progress) => {
          console.log('V150.17: Upload progress:', progress);
        }
      );

      if (result.success && result.photoUrl) {
        invalidateUserPhotoCache(userId);
        updateUserPhoto(userId, result.photoUrl);
        setImageError(false);

        // Notify parent component
        onUploadComplete?.(result.photoUrl);

        console.log('V150.17: Upload successful:', result.photoUrl);
      } else {
        console.error('V150.17: Upload failed:', result.error);
        setImageError(true);
      }
    } catch (error) {
      console.error('V150.17: Upload error:', error);
      setImageError(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  // V160.14.3: Show loading state when loading and no photo
  // V325.17: Local state only — no global context reads during render
  const shouldShowLoading = (isLoading || isUploading) && !displayUrl;
  const config = SIZE_CONFIG[size];

  if (shouldShowLoading) {
    return (
      <div
        className={`${config.className} ${borderClasses[size]} ${className}
                   relative overflow-hidden rounded-2xl border-white/30 shadow-lg
                   bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center`}
        style={{
          // V150.18: Nuclear fix - exact pixel dimensions for perfect 4:5 ratio
          ...(size === 'xl' ? {
            width: '200px',
            height: '250px',
            maxWidth: '90vw',
            maxHeight: 'calc(90vw * 1.25)'
          } : {}),
          aspectRatio: '4/5'
        }}
      >
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
      </div>
    );
  }

  // Check if className includes explicit sizing constraints (modal context)
  // When w-full and h-full are present, parent container constrains dimensions
  // V206.19: Also detect !w-full and !h-full (Tailwind important classes)
  const isConstrainedContext =
    (className.includes('w-full') || className.includes('!w-full')) &&
    (className.includes('h-full') || className.includes('!h-full'));

  // V277.9: Strict URL validation - support both http and blob URLs
  // CRITICAL: Must check displayUrl is truthy AND non-empty string
  const hasValidPhotoUrl = displayUrl &&
    typeof displayUrl === 'string' &&
    displayUrl.length > 0 &&
    (displayUrl.startsWith('http') || displayUrl.startsWith('blob:')) &&
    !imageError;

  // V277.9: Debug logging for empty photo detection
  if (isConstrainedContext && !hasValidPhotoUrl) {
    console.log('🎨 V277.9: No valid photo URL - will render ProfileHeroPhotoEmpty:', {
      displayUrl,
      displayUrlType: typeof displayUrl,
      displayUrlLength: displayUrl?.length,
      imageError,
      hasValidPhotoUrl,
      userName
    });
  }

  // For constrained context, render directly without extra wrapper
  // V207: Fixed to properly inherit parent's aspect ratio (4:5)
  // V220.7: Ensure div fills container completely
  // V277.5: Ensure minimum height for ProfileHeroPhotoEmpty to render properly
  if (isConstrainedContext) {
    return (
      <div
        className={`${className} relative profile-hero-photo`}
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 'inherit',
          boxSizing: 'border-box',
          margin: 0,
          padding: 0,
          width: '100%', // V220.7: Force fill container
          height: '100%', // V220.7: Force fill container
          minHeight: '200px', // V277.5: Ensure minimum height for empty state
          // V325.27: Removed CSS background-image to avoid duplicate browser cache lookup. <img> is the single source.
        }}
        onClick={onClick}
      >
        {hasValidPhotoUrl ? (
          <>
            <img
              src={displayUrl}
              alt={userName || 'User'}
              className="profile-hero-image"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center', // V220: Use center for consistent display on mobile and desktop
                borderRadius: 'inherit',
                margin: 0,
                padding: 0,
                display: 'block'
              }}
              fetchPriority="high"
              decoding="sync"
              onLoad={() => setImageError(false)}
              onError={async (e) => {
                const img = e.currentTarget;
                const failedSrc = img.src;
                console.warn('⚠️ V253: Image load failed:', failedSrc.substring(0, 80));

                // P0: Self-only recovery — broken Cloudinary / stale primary (alt URLs or Google→Cloudinary)
                if (failedSrc.startsWith('http') && currentUser?.id === userId) {
                  const rec = await attemptRecoverSelfProfilePhoto(userId, failedSrc);
                  if (rec.ok && rec.primaryUrl) {
                    const options: CloudinaryOptions = {
                      width: targetDims.w,
                      height: targetDims.h,
                      crop: 'fill',
                      gravity: 'faces',
                      quality: 'auto',
                      format: 'webp',
                    };
                    const base = normalizeCloudinaryBaseUrl(rec.primaryUrl);
                    const optimized = getOptimizedImageUrl(base, options);
                    invalidateUserPhotoCache(userId);
                    setDisplayUrl(optimized);
                    displayUrlRef.current = optimized;
                    setImageError(false);
                    hasLoadedRef.current = true;
                    if (cacheKey) setCachedUrl(cacheKey, optimized);
                    return;
                  }
                }

                // V253: If blob URL fails, clear it and fallback to Cloudinary URL + persist canonical
                if (failedSrc.startsWith('blob:')) {
                  console.log('🔄 V253: Blob URL failed, clearing and falling back to Cloudinary');
                  setFallbackTriggered(true);

                  // Clear blob cache/override
                  try {
                    const { clearOverride } = await import('@/lib/uiPhotoOverride');
                    clearOverride(userId);
                    console.log('✅ V253: Cleared blob override');

                    // Clear PhotoStore blob entry
                    const { invalidate } = await import('@/lib/photoStore');
                    await invalidate(userId);
                    console.log('✅ V253: Cleared PhotoStore blob');

                    // V253: Get Cloudinary URL from DB and persist as canonical
                    const { supabase } = await import('@/integrations/supabase/client');
                    const { data: authData } = await supabase.auth.getUser();
                    let userData: { photo_urls?: unknown } | null = null;
                    if (authData?.user?.id === userId) {
                      const r = await supabase.from('users').select('photo_urls').eq('id', userId).maybeSingle();
                      userData = r.data;
                    } else {
                      const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
                      const { data: rows } = await rpcPeerUsersForViewer(supabase, [userId], { includePrefs: false });
                      userData = rows?.[0] ?? null;
                    }

                    if (userData?.photo_urls && Array.isArray(userData.photo_urls) && userData.photo_urls.length > 0) {
                      const cloudinaryUrl = userData.photo_urls[0];
                      if (typeof cloudinaryUrl === 'string' && cloudinaryUrl.startsWith('http')) {
                        // Persist as canonical URL cache
                        const { setProfilePhotoCachePayload } = await import('@/lib/profilePhotoKeys');
                        setProfilePhotoCachePayload(userId, {
                          kind: 'url',
                          url: cloudinaryUrl,
                          timestamp: Date.now(),
                          ttl: 24 * 60 * 60 * 1000,
                        });
                        console.log('✅ V253: Persisted Cloudinary URL as canonical cache');

                        // Update img src immediately
                        img.src = cloudinaryUrl;
                        setImageError(false);
                        return;
                      }
                    }
                  } catch (err) {
                    console.warn('⚠️ V253: Failed to clear blob cache and fallback:', err);
                  }

                  // Trigger reload with Cloudinary URL
                  setImageError(true);
                  setDisplayUrl('');
                  setIsLoading(true);
                } else {
                  setImageError(true);
                }
              }}
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" style={{ borderRadius: 'inherit' }} />
          </>
        ) : (
          // V277.4: Use professional gradient placeholder for profile hero
          <ProfileHeroPhotoEmpty
            userName={userName}
            onEdit={onClick}
          />
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        className={`${config.className} ${className}
                 relative overflow-hidden rounded-2xl cursor-pointer
                 transition-all duration-200 hover:scale-105`}
        style={{
          ...(className?.includes('!w-full') || className?.includes('w-full') ? {
            width: '100%',
            aspectRatio: '4/5'
          } : size === 'xl' ? {
            width: '200px',
            height: '250px'
          } : {
            aspectRatio: '4/5'
          }),
          overflow: 'hidden',
          position: 'relative',
          display: 'block',
          margin: '0 auto',
          backgroundColor: '#1a1a1a',
        }}
        onClick={onClick}
      >
        {/* V325.16: Real <img> for avatars — browser deduplicates identical src cleanly */}
        {displayUrl && !imageError ? (
          <img
            src={displayUrl}
            alt={userName || 'User'}
            loading="lazy"
            decoding="async"
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
            }}
            onLoad={() => setImageError(false)}
            onError={(ev) => {
              void (async () => {
                const failedSrc = ev.currentTarget.src;
                if (failedSrc.startsWith('http') && currentUser?.id === userId) {
                  const rec = await attemptRecoverSelfProfilePhoto(userId, failedSrc);
                  if (rec.ok && rec.primaryUrl) {
                    const options: CloudinaryOptions = {
                      width: targetDims.w,
                      height: targetDims.h,
                      crop: 'fill',
                      gravity: 'faces',
                      quality: 'auto',
                      format: 'webp',
                    };
                    const base = normalizeCloudinaryBaseUrl(rec.primaryUrl);
                    const optimized = getOptimizedImageUrl(base, options);
                    invalidateUserPhotoCache(userId);
                    setDisplayUrl(optimized);
                    displayUrlRef.current = optimized;
                    setImageError(false);
                    hasLoadedRef.current = true;
                    if (cacheKey) setCachedUrl(cacheKey, optimized);
                    return;
                  }
                }
                setImageError(true);
              })();
            }}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-white/70"
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-2">
              <span className="text-lg font-semibold">
                {userName ? userName.charAt(0).toUpperCase() : ''}
              </span>
            </div>
            <span className="text-xs text-center px-2">
              {fallback || 'No photo'}
            </span>
          </div>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
      </div>

      {/* Upload button — unchanged */}
      {showUploadButton && (
        <div className="absolute -bottom-2 -right-2">
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              disabled={isUploading}
            />
            <div className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full shadow-lg
                          transition-all duration-200 hover:scale-110 border-2 border-white">
              {isUploading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <Camera className="w-4 h-4" />
              )}
            </div>
          </label>
        </div>
      )}
    </div>
  );
};

// V150.22: MEMO WRAPPER - Prevent unnecessary re-renders that cause photo switching
// V267.3: Updated to check photoUrls changes to prevent cancelled requests
export const UserPhoto = memo(UserPhotoComponent, (prev, next) => {
  // Re-render if userId OR photoUrls changes
  const userIdSame = prev.userId === next.userId;
  const photoUrlsSame = JSON.stringify(prev.photoUrls) === JSON.stringify(next.photoUrls);
  return userIdSame && photoUrlsSame;
});

/**
 * V150.17: Specialized variants for common use cases
 */

// V206.8: Profile hero photo - responsive sizing for mobile + desktop
export const ProfileHeroPhoto: React.FC<Omit<UserPhotoProps, 'size'>> = memo((props) => {
  // V325.19: Gated — was logging on every render, creating GC pressure
  if (import.meta.env.DEV && false) { // Set to true when debugging photo issues
    console.log('[V277.9] 📸 ProfileHeroPhoto rendering:', {
      userId: props.userId,
      photoUrlsLength: props.photoUrls?.length,
    });
  }

  // V206.8: Override className to make photo responsive (fill container)
  return (
    <UserPhoto
      {...props}
      size="xl"
      className={`${props.className || ''} !w-full !h-full`}
    />
  );
});
ProfileHeroPhoto.displayName = 'ProfileHeroPhoto';

// Navigation/header photo
export const NavPhoto: React.FC<Omit<UserPhotoProps, 'size'>> = (props) => (
  <UserPhoto {...props} size="small" />
);

// Card/list photo
export const CardPhoto: React.FC<Omit<UserPhotoProps, 'size'>> = (props) => (
  <UserPhoto {...props} size="medium" />
);

// Modal/dialog photo
export const ModalPhoto: React.FC<Omit<UserPhotoProps, 'size'>> = (props) => (
  <UserPhoto {...props} size="large" />
);

export default UserPhoto;
